// Exact BigInt port of contracts/src/libraries/WorkMath.sol (difficulty arithmetic).
// Any change here must keep `protocol/test/workMath.test.ts` (Solidity-generated vectors) green.
import {
  IDLE_GRACE,
  IDLE_HALFLIFE,
  MAX_AGE_COUNTED,
  MAX_STEP,
  MAX_TARGET,
  MAX_UINT256,
  MIN_TARGET,
  RETARGET_TAU,
  TARGET_INTERVAL,
} from "./params";

export function clampTarget(t: bigint): bigint {
  if (t < MIN_TARGET) return MIN_TARGET;
  if (t > MAX_TARGET) return MAX_TARGET;
  return t;
}

/** target * 2^(e16/65536), clamped. Uses the aserti3-2d cubic for the fractional part. */
export function scale(target: bigint, e16: bigint): bigint {
  if (target > MAX_TARGET) target = MAX_TARGET;
  // arithmetic shift == floor division for negative numbers (BigInt >> is arithmetic)
  const shifts = e16 >> 16n;
  const f = e16 & 0xffffn;
  const factor = 65536n + ((195766423245049n * f + 971821376n * f * f + 5127n * f * f * f + (1n << 47n)) >> 48n);
  let t = target * factor;
  if (shifts >= 0n) {
    if (shifts >= 64n || t > MAX_UINT256 >> shifts) return MAX_TARGET;
    t = (t << shifts) >> 16n;
  } else {
    const s = -shifts + 16n;
    t = s >= 256n ? 0n : t >> s;
  }
  return clampTarget(t);
}

/** Solidity signed division truncates toward zero; BigInt division does too. */
function sdiv(a: bigint, b: bigint): bigint {
  return a / b;
}

export function effectiveTarget(openTarget: bigint, age: bigint): bigint {
  if (age <= IDLE_GRACE) return openTarget;
  const over = age - IDLE_GRACE;
  if (over > 256n * IDLE_HALFLIFE) return MAX_TARGET;
  return scale(openTarget, (over << 16n) / IDLE_HALFLIFE);
}

export function nextTarget(openTarget: bigint, usedTarget: bigint, age: bigint): bigint {
  const a = age < MAX_AGE_COUNTED ? age : MAX_AGE_COUNTED;
  const e16 = sdiv((a - TARGET_INTERVAL) << 16n, RETARGET_TAU);
  let t = scale(usedTarget, e16);
  const lo = openTarget / MAX_STEP;
  const hi = openTarget * MAX_STEP > MAX_UINT256 ? MAX_UINT256 : openTarget * MAX_STEP;
  if (t < lo) t = lo;
  if (t > hi) t = hi;
  return clampTarget(t);
}

/** floor(2^256 / (target + 1)) — expected hashes per valid proof. */
export function difficulty(target: bigint): bigint {
  return (1n << 256n) / (target + 1n);
}

/**
 * Seconds (relative to challenge open) at which the effective target first reaches `hash`,
 * i.e. when a found hash becomes a valid proof through idle decay. Returns 0 if already valid,
 * null if it can never become valid (hash above MAX_TARGET).
 */
export function secondsUntilValid(openTarget: bigint, age: bigint, hash: bigint): bigint | null {
  if (hash <= effectiveTarget(openTarget, age)) return 0n;
  if (hash > MAX_TARGET) return null;
  let lo = age;
  let hi = IDLE_GRACE + 256n * IDLE_HALFLIFE + 1n;
  while (hi - lo > 1n) {
    const mid = (lo + hi) / 2n;
    if (hash <= effectiveTarget(openTarget, mid)) hi = mid;
    else lo = mid;
  }
  return hi - age;
}

/** Human-friendly difficulty as a float (for display only). */
export function difficultyFloat(target: bigint): number {
  return 2 ** 256 / (Number(target) + 1);
}
